import type { JSX } from 'react';
import { failureFrom } from '@/lib/result';
import { clockTime, monthDay } from '@/lib/format';
import type { Me } from '../auth/use-me';
import { card, eyebrow, pageTitle } from '../shell/look';
import { ErrorState, LoadingState } from '../shell/states';
import { AgentEditor } from './agent-editor';
import { lastChange } from './agent-schema';
import { useAgentSettings } from './use-agent';

/** Agent settings (design L457–506): the form, and a note on how it applies. */
export function AgentPage({ me }: { me: Me }): JSX.Element {
  const agent = useAgentSettings();
  return (
    <section className="animate-rise grid items-start gap-6 desk:grid-cols-[minmax(0,1fr)_300px]">
      <div>
        <div className={eyebrow}>BEHAVIOUR</div>
        <h1 className={`${pageTitle} mb-6`}>Agent settings</h1>
        {agent.isPending && <LoadingState rows={6} />}
        {agent.isError && (
          <ErrorState {...failureFrom(agent.error)} onRetry={() => void agent.refetch()} />
        )}
        {agent.data !== undefined && (
          <AgentEditor
            key={agent.data.settings.updatedAt ?? 'defaults'}
            loaded={agent.data}
            admin={me.role === 'admin'}
          />
        )}
      </div>
      <aside className={`${card} text-mute p-[22px] text-[13.5px] leading-[1.55] desk:mt-[62px]`}>
        <div className="text-ink mb-2 font-semibold">How these apply</div>
        Settings apply to every interface at once. Telegram replies are trimmed to 4,000 characters
        automatically. The system prompt is prepended before any retrieved source context.
        <div className="border-line mt-4 border-t pt-3.5 font-mono text-[11.5px]">
          {agent.data === undefined
            ? 'Last change · …'
            : lastChange(agent.data.settings, (iso) => `${monthDay(iso)}, ${clockTime(iso)}`)}
        </div>
      </aside>
    </section>
  );
}
